import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Phone, Mail, User, Loader2, Copy, Send, X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

/** Customer database — search by phone/email/name, view details + all bookings,
 * open new manual booking pre-filled, or (re-)send payment link for any pending. */
const AdminCustomers = ({ onCreateManualBooking }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEmail, setOpenEmail] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sendingLinkId, setSendingLinkId] = useState(null);

  const search = async (query) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers`, { params: { q: query || undefined } });
      setRows(data.customers || []);
    } catch {
      toast.error("Search failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(""); }, []);
  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const openDetail = async (email) => {
    setOpenEmail(email);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers/${encodeURIComponent(email)}`);
      setDetail(data);
    } catch {
      toast.error("Could not load customer.");
      setOpenEmail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const sendPaymentLink = async (bookingId) => {
    setSendingLinkId(bookingId);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${bookingId}/payment-link`);
      if (data.emailed) toast.success("Payment link emailed to the guest.");
      else toast.success("Link ready — email delivery failed, copy manually below.");
      // Copy to clipboard as a bonus
      try { await navigator.clipboard.writeText(data.checkout_url); } catch {}
      // Refresh detail so link is visible
      if (openEmail) openDetail(openEmail);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not generate link.");
    } finally {
      setSendingLinkId(null);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed — long-press to select instead.");
    }
  };

  return (
    <section className="admin-card" data-testid="admin-customers-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="overline text-[#B76E79] flex items-center gap-2"><span className="marker-line marker-line--rose" /> Contact directory</p>
          <h3 className="font-serif text-2xl md:text-3xl mt-1">Customer database</h3>
          <p className="text-sm text-[#6A5F58] mt-1">Search by phone number, name or email. Open a customer to see all bookings, resend a payment link, or start a new booking.</p>
        </div>
      </div>

      <div className="relative max-w-lg">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A5F58]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search 07123, jane@…, or 'Smith'"
          className="pl-10 h-12 rounded-lg"
          data-testid="customers-search"
        />
      </div>

      <div className="mt-6">
        {loading && (
          <div className="text-center py-8 text-[#6A5F58]"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading customers…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-center py-10 text-[#6A5F58]" data-testid="customers-empty">
            No customers found{q ? ` for "${q}"` : ""}.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="rounded-xl border border-[#DDD1BE] overflow-hidden" data-testid="customers-list">
            <table className="w-full text-sm">
              <thead className="bg-[#F5EFE6]">
                <tr className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-right px-4 py-3">Bookings</th>
                  <th className="text-right px-4 py-3">Spent</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.email}
                    className="border-t border-[#DDD1BE] hover:bg-[#FBF7F1] cursor-pointer"
                    onClick={() => openDetail(c.email)}
                    data-testid={`customer-row-${c.email}`}
                  >
                    <td className="px-4 py-3 font-medium">{c.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {c.phone ? (
                        <a href={`tel:${c.phone.replace(/\s/g, "")}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#B76E79]">{c.phone}</a>
                      ) : <span className="text-[#6A5F58]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#6A5F58] text-xs">{c.email}</td>
                    <td className="px-4 py-3 text-right">{c.total_bookings}</td>
                    <td className="px-4 py-3 text-right">{gbp(c.total_spent)}</td>
                    <td className="px-4 py-3 text-right"><span className="text-[#B76E79] text-xs uppercase tracking-[0.14em]">Open →</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer detail drawer */}
      {openEmail && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={() => setOpenEmail(null)}>
          <div className="w-full max-w-2xl h-full bg-[#F5EFE6] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="customer-drawer">
            <div className="sticky top-0 bg-[#F5EFE6] border-b border-[#DDD1BE] p-5 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">Customer</p>
                <h4 className="font-serif text-2xl">{detail?.name || openEmail}</h4>
              </div>
              <button onClick={() => setOpenEmail(null)} className="p-2 rounded-full hover:bg-[#DDD1BE]" data-testid="customer-drawer-close"><X size={18} /></button>
            </div>
            {detailLoading && <div className="p-8 text-center text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}
            {detail && (
              <div className="p-5 md:p-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Phone</p>
                    <a href={`tel:${(detail.phone || "").replace(/\s/g, "")}`} className="mt-1 flex items-center gap-1 font-mono text-sm text-[#14100F] hover:text-[#B76E79]">
                      <Phone size={13} /> {detail.phone || "—"}
                    </a>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Email</p>
                    <a href={`mailto:${detail.email}`} className="mt-1 flex items-center gap-1 text-xs text-[#14100F] hover:text-[#B76E79] break-all">
                      <Mail size={13} /> {detail.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Loyalty</p>
                    <p className="mt-1 text-xs">
                      {detail.ledger ? (
                        <>Camp {detail.ledger.touring_nights || 0}/7 · Lodge {detail.ledger.lodge_nights || 0}/10</>
                      ) : (
                        <span className="text-[#6A5F58]">no ledger</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      onCreateManualBooking?.({
                        guest_name: detail.name || "",
                        guest_email: detail.email,
                        guest_phone: detail.phone || "",
                      });
                      setOpenEmail(null);
                    }}
                    className="btn-primary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5"
                    data-testid="customer-new-booking"
                  >
                    <Plus size={13} /> New booking for {detail.name?.split(" ")[0] || "them"}
                  </button>
                </div>

                <div>
                  <p className="overline text-[#B76E79] mb-3">Bookings ({detail.total_bookings || 0})</p>
                  <div className="space-y-2">
                    {(detail.bookings || []).map((b) => {
                      const isPending = b.payment_status === "pending";
                      return (
                        <div key={b.id} className="bg-white border border-[#DDD1BE] rounded-lg p-3 text-sm" data-testid={`customer-booking-${b.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <p className="font-medium">{b.unit_id} · {b.start_date} → {b.end_date}</p>
                              <p className="text-[11px] text-[#6A5F58]">{b.guests} guest{b.guests > 1 ? "s" : ""} · {b.nights} night{b.nights > 1 ? "s" : ""} · {gbp(b.total)}</p>
                              {b.vehicle_reg && <p className="text-[10px] font-mono mt-1">🚗 {b.vehicle_reg}</p>}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold ${
                                b.payment_status === "paid" ? "bg-[#3B8C4E] text-white" :
                                b.payment_status === "pending" ? "bg-[#C89B5F] text-white" :
                                "bg-[#DDD1BE] text-[#14100F]"
                              }`}>
                                {b.payment_status}
                              </span>
                              {isPending && (
                                <>
                                  <button
                                    onClick={() => sendPaymentLink(b.id)}
                                    disabled={sendingLinkId === b.id}
                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white disabled:opacity-50"
                                    data-testid={`send-payment-${b.id}`}
                                  >
                                    {sendingLinkId === b.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                                    Send payment link
                                  </button>
                                  {b.checkout_url && (
                                    <button
                                      onClick={() => copyLink(b.checkout_url)}
                                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-white"
                                      data-testid={`copy-payment-${b.id}`}
                                    >
                                      <Copy size={10} /> Copy link
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminCustomers;
